import React, { useState, FormEvent } from 'react';
import { useNavigate, Navigate } from 'react-router-dom';
import { useAuth, appUrls } from '@rehome/shared';
import { PawPrint, Mail, Lock, Eye, EyeOff, AlertCircle, Loader2, X } from 'lucide-react';

// Cross-app URL comes from the shared config (shared/src/config.ts).
const PLATFORM_URL = appUrls.platform;

/** Shelter Login — Supabase Auth, redirects to /global after login. No registration — shelter admins are created by superadmin. */
const LoginPage: React.FC = () => {
  const { signIn, signOut, resetPassword, user, userProfile, loading, error, clearError } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPw, setShowPw] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [resetSent, setResetSent] = useState(false);

  if (loading) return <div className="min-h-screen flex items-center justify-center bg-gray-50"><Loader2 className="w-8 h-8 text-teal-600 animate-spin" /></div>;

  // After login: check user_type and password_change_required
  if (user && userProfile) {
    if (userProfile.user_type !== 'shelter') {
      // Wrong namespace — sign out
      signOut();
      return (
        <div className="min-h-screen flex items-center justify-center bg-gray-50">
          <div className="text-center">
            <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
            <h2 className="text-xl font-bold text-gray-900 mb-2">Nincs hozzáférés</h2>
            <p className="text-gray-500">Ez a felület csak menhelyi munkatársak számára érhető el.</p>
          </div>
        </div>
      );
    }
    if (userProfile.password_change_required) {
      return <Navigate to="/set-password" replace />;
    }
    return <Navigate to="/global" replace />;
  }

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    const { error: err } = await signIn(email, password);
    setSubmitting(false);
    if (!err) navigate('/global');
  };

  const handleForgotPassword = async () => {
    if (!email) { clearError(); return; }
    await resetPassword(email);
    setResetSent(true);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-teal-50 to-emerald-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-xl border border-gray-100 w-full max-w-md p-8">
        <div className="text-center mb-8">
          <div className="bg-teal-600 p-3 rounded-xl inline-flex mb-4"><PawPrint className="w-8 h-8 text-white" /></div>
          <h1 className="text-2xl font-bold text-gray-900">Menhelyi Belépés</h1>
          <p className="text-gray-500 text-sm mt-1">ReHome Shelter Platform</p>
        </div>

        {resetSent && (
          <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-xl text-sm mb-6">
            Ha a megadott email regisztrálva van, küldtünk egy jelszó-visszaállító linket. Ellenőrizd a postaládádat!
          </div>
        )}

        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-xl flex items-center gap-2 mb-6 text-sm">
            <AlertCircle className="w-4 h-4 flex-shrink-0" /><span className="flex-1">{error}</span>
            <button onClick={clearError} className="text-red-400 hover:text-red-600" aria-label="Hiba bezárása"><X className="w-4 h-4" /></button>
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Email cím</label>
            <div className="relative">
              <Mail className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4" />
              <input type="email" required value={email} onChange={e => setEmail(e.target.value)}
                className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-teal-500 outline-none" placeholder="admin@menhely.hu" />
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Jelszó</label>
            <div className="relative">
              <Lock className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4" />
              <input type={showPw ? 'text' : 'password'} required value={password} onChange={e => setPassword(e.target.value)}
                className="w-full pl-10 pr-12 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-teal-500 outline-none" placeholder="••••••••" />
              <button type="button" onClick={() => setShowPw(!showPw)} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
                {showPw ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
          </div>
          <button type="submit" disabled={submitting}
            className="w-full bg-teal-600 text-white py-3 rounded-xl font-bold hover:bg-teal-700 transition-colors disabled:opacity-50 flex items-center justify-center gap-2">
            {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : null} Bejelentkezés
          </button>
        </form>

        <button onClick={handleForgotPassword} className="w-full text-center text-sm text-gray-400 hover:text-teal-600 mt-3 transition-colors">
          Elfelejtett jelszó?
        </button>

        <p className="text-center text-sm text-gray-400 mt-6">
          Csak menhelyi munkatársak számára. <a href={`${PLATFORM_URL}`} className="text-teal-600 hover:underline">Vissza a Platformhoz</a>
        </p>
      </div>
    </div>
  );
};

export default LoginPage;
